import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import './UsersPage.css';
import { useGetUsersQuery, useGetUserCountriesQuery } from 'store/services/admin/adminApi';
import AdminLayout from '../AdminLayout';
import ChevronDownIcon from 'components/Icons/ChevronDownIcon';
import { AssetUrls } from 'config/assets';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import CountryFilterSelect from './components/CountryFilterSelect';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'elite', label: 'Elite' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'new', label: 'New' },
];

const UsersPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [addressTerm, setAddressTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState(''); // '' = All
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const debouncedAddress = useDebouncedValue(addressTerm, 300);

  // Reset to page 1 whenever any active filter changes so we don't strand the
  // admin on a now-empty page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedAddress, countryFilter, filter]);

  const { data, isLoading, error } = useGetUsersQuery({
    page,
    pageSize: 50,
    search: debouncedSearch,
    filter,
    address: debouncedAddress,
    country: countryFilter,
  });

  const { data: countryOptions = [] } = useGetUserCountriesQuery();

  const handleFilterChange = (value: string) => {
    setFilter(value);
    setIsFilterDropdownOpen(false);
    setPage(1);
  };

  const getSelectedFilterLabel = () => {
    const selected = FILTER_OPTIONS.find(opt => opt.value === filter);
    return selected ? selected.label : 'All';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatPoints = (points: number) => {
    return points.toLocaleString();
  };

  const getAccountStatus = (user: any) => {
    // Priority: Deleted > Banned > Verification Status
    if (user.status === 'deleted') {
      return <div className="status-tag status-tag-deleted">deleted</div>;
    }

    if (user.isBanned) {
      return <div className="status-tag status-tag-banned">banned</div>;
    }

    const tags = [];

    // Check email verification
    if (!user.emailVerified) {
      tags.push(<div key="email" className="status-tag status-tag-warning">email</div>);
    }

    // Only check phone verification if the field exists in the API response
    // Note: /admin/users endpoint doesn't return phoneVerified, but /admin/users/:id does
    if (user.phoneVerified !== undefined && !user.phoneVerified) {
      tags.push(<div key="mobile" className="status-tag status-tag-warning">mobile</div>);
    }

    if (tags.length > 0) {
      return <div className="status-tags-container">{tags}</div>;
    }

    // Consider verified if email is verified (phone verification status unknown from list endpoint)
    return <div className="status-tag status-tag-verified">verified</div>;
  };

  // Mock data for demo - replace with actual API data when backend is ready
  const mockUsers = [
    {
      id: 1346134,
      username: 'SamuraiFunn',
      firstName: 'Eric',
      lastName: 'Freytag',
      email: 'samuraifunn@gmail.com',
      points: 1903000,
      createdAt: '2025-07-11',
      lastLogin: '2025-07-11',
      avatarUrl: AssetUrls.avatars.avatar1,
      emailVerified: true,
      phoneVerified: true,
      isBanned: false,
      referralSource: 'google',
      isElite: true
    },
    {
      id: 5314143,
      username: 'Gosta Dirk',
      firstName: 'Avery',
      lastName: 'Collins',
      email: 'avery.collins@magicmail.cc',
      points: 1903000,
      createdAt: '2025-07-11',
      lastLogin: '2025-07-11',
      avatarUrl: AssetUrls.avatars.avatar2,
      emailVerified: false,
      phoneVerified: true,
      isBanned: false,
      referralSource: 'reddit'
    },
    {
      id: 763657,
      username: 'Angus MacKenzie',
      firstName: 'Jamal',
      lastName: 'Rivers',
      email: 'jamal.rivers88@gmail.com',
      points: 1903000,
      createdAt: '2025-07-11',
      lastLogin: '2025-07-11',
      avatarUrl: AssetUrls.avatars.avatar3,
      emailVerified: true,
      phoneVerified: false,
      isBanned: false,
      referralSource: 'twitter'
    },
    {
      id: 265426,
      username: 'CraterHoof B',
      firstName: 'Clara',
      lastName: 'Mendoza',
      email: 'clara_m@decklist.net',
      points: 1903000,
      createdAt: '2025-07-11',
      lastLogin: '2025-07-11',
      avatarUrl: AssetUrls.avatars.avatar4,
      emailVerified: false,
      phoneVerified: false,
      isBanned: false,
      referralSource: null
    },
    {
      id: 25465,
      username: 'Jasmine Boreal',
      firstName: 'Eliot',
      lastName: 'Tanaka',
      email: 'eli.tnk2025@outlook.com',
      points: 1903000,
      createdAt: '2025-07-11',
      lastLogin: '2025-07-11',
      avatarUrl: AssetUrls.avatars.avatar5,
      emailVerified: false,
      phoneVerified: true,
      isBanned: true,
      referralSource: 'friend'
    },
    {
      id: 27524623,
      username: 'Tal Tekahashi',
      firstName: 'Nina',
      lastName: 'Patel',
      email: 'nina.patel.trades@fastmail.com',
      points: 1903000,
      createdAt: '2025-07-11',
      lastLogin: '2025-07-11',
      avatarUrl: AssetUrls.avatars.avatar6,
      emailVerified: true,
      phoneVerified: true,
      isBanned: false,
      referralSource: 'youtube'
    }
  ];

  // Use API data if available, otherwise use mock data
  const users: any[] = data?.users || mockUsers;

  return (
    <AdminLayout title="Admin Panel - Manager Users" currentPath="/admin/users">
      {/* @ts-expect-error - React 19 types issue with children */}
      <div className="users-page">
          <div className="users-header">
          <h2 className="users-title">User Search</h2>
          <div className="users-search-container">
            <input
              type="text"
              placeholder="Username, Email, First Name, Last Name, User ID"
              className="users-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <input
              type="text"
              placeholder="Search address (any line, city, region, postal)"
              className="users-search-input users-address-input"
              value={addressTerm}
              onChange={(e) => setAddressTerm(e.target.value)}
            />
            <div className="users-country-filter">
              <CountryFilterSelect
                value={countryFilter}
                onChange={setCountryFilter}
                options={countryOptions}
              />
            </div>
            <div
              className="users-filter-dropdown"
              onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
            >
              <span>{getSelectedFilterLabel()}</span>
              <ChevronDownIcon className="dropdown-arrow" />
              {isFilterDropdownOpen && (
                <div className="filter-dropdown-menu">
                  {FILTER_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`filter-dropdown-item ${filter === option.value ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange(option.value);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Points</th>
                <th>Join Date</th>
                <th>Last Login</th>
                <th>User_ID</th>
                <th>Email</th>
                <th>Source</th>
                <th>Status</th>
                <th>Elite</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user: any) => (
                  <tr
                    key={user.id}
                    className="clickable-row"
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                  >
                    <td>
                      <div className="user-cell">
                        <AvatarWithFlag
                          avatarUrl={user.avatarUrl}
                          username={user.username}
                          countryCode={user.shippingCountry || user.country}
                          size={32}
                        />
                        <span>{user.username}</span>
                      </div>
                    </td>
                  <td>{
                    user.firstName || user.lastName 
                      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                      : '-'
                  }</td>
                  <td>
                    <div className="points-cell">
                      <div className="points-icon" />
                      <span>{formatPoints(user.points)}</span>
                    </div>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>{formatDate(user.lastLogin || user.createdAt)}</td>
                    <td>{user.id}</td>
                    <td>{user.email}</td>
                    <td>{user.referralSource || '-'}</td>
                    <td>{getAccountStatus(user)}</td>
                    <td>
                      {user.isElite ? (
                        <img
                          src={AssetUrls.icons.elite.badge}
                          alt="Elite"
                          className="elite-badge-cell"
                        />
                      ) : null}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isLoading && (
          <div className="users-loading">Loading users...</div>
        )}

        {error && (
          <div className="users-error">Error loading users. Please try again.</div>
        )}

        {data?.pagination && data.pagination.totalPages > 0 && (
          <div className="users-pagination">
            <button
              className="pagination-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {data.pagination.currentPage} of {data.pagination.totalPages}
              {' '}({data.pagination.totalUsers} total users)
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= data.pagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default UsersPage;